import React, { FC, useEffect, useState } from 'react'
import './ShoppingCart.scss'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import PublicAppContainer from 'app_components/layouts/public'
import { useDispatch, useSelector } from 'react-redux'
import { CombinedReducers } from 'app_events/index'
import { BUY_ACTION, IObjectToBuy, IShoppingCarItem, PROCESS_PURCHASE } from 'app_types/shoppingCart/shoppingCarTypes'
import ShoppingCartItem from 'app_components/commons/shoppingCartItem/ShoppingCartItem'
import ButtonWithLogin from 'app_components/modules/ButtonWithLogin/ButtonWithLogin'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import ShoppingCartService, { IBillingInfo, PurchaseProcessType } from 'app_services/shoppingCart/shoppingCart'
import { sleep } from 'app_utils/functions/sleep'
import { shoppingCarChange } from 'app_events/app/actions'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import { IActionsCart } from 'app_events/app/types'
import { Link } from 'react-router-dom'
import { formatPrice } from 'app_utils/coursePrice'
import StepperComponent, { Step } from 'app_components/commons/stepper/Stepper'
import BillingForm, { BillingFormData } from './BillingForm'
import OrderSummaryConfirmation from './OrderSummaryConfirmation'
import { EfipayCurrency } from 'app_types/efipay/efipayTypes'
import ModalReviewPurchase from './ModalReviewPurchase'

const shoppingCartEmptyImage = require('app_assets/images/resources/shopping_cart_empty.png')

// Definición de los pasos del carrito
const CART_STEPS: Step[] = [
  { id: 'cart', label: 'Carrito', description: 'Revisa tus productos' },
  { id: 'billing', label: 'Facturación', description: 'Información de pago' },
  { id: 'confirmation', label: 'Confirmación', description: 'Revisa tu pedido' },
];

interface IShoppingCartProps {}

const ShoppingCart: FC<IShoppingCartProps> = () => {
  const auth = useSelector((state: any) => state.auth ? state.auth : null)
  const isAuth = useSelector((store: CombinedReducers) => store.auth.isAuth);
  const screenNotifications = new ScreenNotificationsUtil()
  const shoppingCartService = new ShoppingCartService()
  const dispatch = useDispatch()
  const {action: lastAction, data: shoppingCarItems}: {action: IActionsCart, data: IShoppingCarItem[]} = useSelector((store: CombinedReducers) => store?.app?.shoppingCart ?? {action: 'none', data: []});
  const [modalReviewDataState, setModalReviewDataState] = useState<boolean>(false)
  const [modalReviewData, setModalReviewData] = useState<Record<string, IObjectToBuy>>({})
  const [currentStep, setCurrentStep] = useState<string>('cart')
  const [billingData, setBillingData] = useState<BillingFormData | null>(null)
  const [isBillingFormValid, setIsBillingFormValid] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [reviewData, setReviewData] = useState<Record<string, IObjectToBuy>>({});
  const [hasReviewedPurchase, setHasReviewedPurchase] = useState<boolean>(false);

  // Efecto para simular el proceso de compra cuando se llega al paso de confirmación
  useEffect(() => {
    const simulatePurchaseProcess = async () => {
      if (currentStep === 'confirmation' && !hasReviewedPurchase && billingData) {
        try {
          // Mostrar indicador de carga
          screenNotifications.fireProcessing({
            title: 'Verificando disponibilidad...',
            text: 'Por favor, espera mientras verificamos la disponibilidad de los cursos.'
          });

          // Simular el proceso de compra para obtener validaciones
          const response = await shoppingCartService.buyCourses(
            shoppingCarItems.map((item) => {
              item.buyAction =  BUY_ACTION.FOR_MYSELF
              return item
            }),
            getBillingInfo(),
            PurchaseProcessType.CHECK_CONDITIONS,
            BUY_ACTION.FOR_MYSELF,
            auth?.user?.data?.id ?? null
          );

          // Cerrar el indicador de carga
          screenNotifications.close();

          if (response && response.status === 'success' && response.processPurchase === PROCESS_PURCHASE.VERIFY_PURCHASE && response.review) {
            // Guardar los datos de revisión
            setReviewData(response.review);
            setHasReviewedPurchase(true);
          }
        } catch (error) {
          screenNotifications.close();
          console.error('Error al verificar la disponibilidad:', error);
          screenNotifications.fire({
            status: 'error',
            message: 'Ha ocurrido un error al verificar la disponibilidad de los cursos.'
          });
        }
      }
    };

    simulatePurchaseProcess();
  }, [currentStep, hasReviewedPurchase]);

  // Resetear el estado de revisión cuando se cambia de paso
  useEffect(() => {
    if (currentStep !== 'confirmation') {
      setHasReviewedPurchase(false);
      setReviewData({});
    }
  }, [currentStep]);

  // Añadir este efecto para monitorear cambios en la autenticación
  useEffect(() => {
    // Si el usuario no está autenticado y está en un paso que requiere autenticación
    if (!isAuth && (currentStep === 'confirmation')) {
      // Mostrar mensaje informativo
      screenNotifications.fireToast(
        {
          status: 'warning',
          message: 'Se requiere iniciar sesión para continuar con la compra.',
        },
        {
          position: 'bottom-right',
        }
      );

      // Redirigir al primer paso
      setCurrentStep('cart');
    }
  }, [isAuth, currentStep]);

  useEffect(() => {
    if (lastAction === 'add' && modalReviewDataState) {
      setModalReviewDataState(false)
      setModalReviewData({})
    }
  }, [shoppingCarItems, lastAction])

  const getBillingInfo = (): IBillingInfo => {
    return {
      fullName: `${billingData?.firstName} ${billingData?.lastName}`,
      firstName: billingData?.firstName ?? '',
      lastName: billingData?.lastName ?? '',
      docNumber: billingData?.docNumber ?? '',
      docType: billingData?.docType ?? '',
      nature: billingData?.nature ?? '',
      classification: billingData?.classification ?? '',
      country: billingData?.country?.value ?? '',
      department: billingData?.department?.value ?? '',
      city: billingData?.city?.value ?? '',
      currency: billingData?.currency ?? EfipayCurrency.COP,
      email: billingData?.email ?? '',
    };
  }

  const handleStepChange = (stepId: string) => {
    // Solo permitir cambiar a pasos anteriores
    const currentIndex = CART_STEPS.findIndex(step => step.id === currentStep);
    const newIndex = CART_STEPS.findIndex(step => step.id === stepId);

    if (newIndex < currentIndex) {
      setCurrentStep(stepId);
    }
  }

  const goToNextStep = () => {
    const currentIndex = CART_STEPS.findIndex(step => step.id === currentStep);

    // Validar que el formulario de facturación esté completo antes de avanzar
    if (currentStep === 'billing' && !isBillingFormValid) {
      screenNotifications.fireToast(
        {
          status: 'error',
          message: 'Por favor, completa todos los campos obligatorios de facturación',
        },
        {
          position: 'bottom-right',
        }
      );
      return;
    }

    // Validar que se hayan aceptado los términos y condiciones
    if (currentStep === 'confirmation' && !termsAccepted) {
      screenNotifications.fireToast(
        {
          status: 'error',
          message: 'Debes aceptar los términos y condiciones para continuar',
        },
        {
          position: 'bottom-right',
        }
      );
      return;
    }

    if (currentIndex < CART_STEPS.length - 1) {
      setCurrentStep(CART_STEPS[currentIndex + 1].id);
    } else {
      endingBuy();
    }
  }

  const handleBillingFormChange = (data: BillingFormData, isValid: boolean) => {
    setBillingData(data);
    setIsBillingFormValid(isValid);
  }

  const getButtonTextByStep = () => {
    switch (currentStep) {
      case 'cart':
        return 'Continuar a facturación';
      case 'billing':
        return 'Revisar pedido';
      case 'confirmation':
        return 'Proceder al pago';
      default:
        return 'Continuar';
    }
  }

  const renderShoppingCartEmpty = () => {
    return (
      <div className='shopping-cart-empty_container'>
        <h3>Tu carrito está vacío</h3>
        <img src={shoppingCartEmptyImage} alt="Empty Shopping Cart" />
        <p>¡Empieza un carrito de compras!</p>
        <Link to="/academic-filter">
          <ButtonComponent variant='secondary'>
            Ver programas
          </ButtonComponent>
        </Link>
      </div>
    )
  }

  const renderItems = () => {
    if (shoppingCarItems.length === 0)
      return renderShoppingCartEmpty()

    let component: React.ReactNode = null;

    component = shoppingCarItems
      .sort((a, b) => {
        return (b?.dateOfAddition ?? 0) - (a?.dateOfAddition ?? 0);
      })
      .map((item: IShoppingCarItem) => <ShoppingCartItem itemToBuy={item}></ShoppingCartItem>)
    return component
  }

  const getProductsCount = () => {
    const count = shoppingCarItems.map((item) => item.numberOfPlaces).reduce((acc, curr) => acc + curr, 0);
    if (count === 1) {
      return `${count} item`
    } else if (count > 1) {
      return `${count} items`
    }
  }

  const getTotalPrice = () => {
    const price = shoppingCarItems.reduce((accum, item) => {
      let itemPrice = 0;
      if (item.priceWithDiscountNumeric !== undefined && item.priceWithDiscountNumeric !== null) {
        itemPrice = item.priceWithDiscountNumeric;
      } else if (item.priceNumeric) {
        itemPrice = item.priceNumeric;
      }

      accum += itemPrice * item.numberOfPlaces;
      return accum;
    }, 0)
    return price
  }

  const handleMainButtonClick = () => {
    goToNextStep();
  }

  const endingBuy = () => {
    // Determinar los tipos de compras en el carrito
    const hasForMyself = shoppingCarItems.some(item => item.buyAction === BUY_ACTION.FOR_MYSELF);
    const hasForOthers = shoppingCarItems.some(item => item.buyAction === BUY_ACTION.FOR_OTHERS);
    const hasForMeAndOthers = shoppingCarItems.some(item => item.buyAction === BUY_ACTION.FOR_ME_AND_OTHERS);

    // Personalizar el mensaje según la combinación de tipos de compra
    let confirmMessage = '';
    let confirmTitle = '¿Estás seguro de proceder al pago?';

    if ((hasForMyself || hasForMeAndOthers) && !hasForOthers) {
      // Solo compras para mí o para mí y otros
      confirmMessage = `Estás a punto de realizar la compra de ${getProductsCount()}. Una vez completado el pago, tendrás acceso inmediato a los programas adquiridos para ti. ¿Deseas continuar con el proceso de pago?`;
    } else if (!hasForMyself && !hasForMeAndOthers && hasForOthers) {
      // Solo compras para otros
      confirmMessage = `Estás a punto de realizar la compra de ${getProductsCount()} para otras personas. Una vez completado el pago, podrás inscribir a los beneficiarios. ¿Deseas continuar con el proceso de pago?`;
    } else {
      // Compra mixta (algunos para mí y algunos para otros)
      confirmMessage = `Estás a punto de realizar la compra de ${getProductsCount()}, algunos para ti y otros para terceros. Una vez completado el pago, tendrás acceso inmediato a tus programas y podrás inscribir a los beneficiarios. ¿Deseas continuar con el proceso de pago?`;
    }

    const confirm = screenNotifications.fire({
      message: confirmMessage,
    },
    {
      type: 'warning',
      title: confirmTitle,
      confirmButtonText: `Sí, proceder al pago`,
      cancelButtonText: `Cancelar`,
      showCancelButton: true,
    })

    if (confirm) {
      confirm.then(async (result) => {
        if (result.value) {
          purchaseProcess(PurchaseProcessType.PURCHASE)
        }
      })
    }
  }

  const purchaseProcess = async (processType: PurchaseProcessType = PurchaseProcessType.CHECK_CONDITIONS, cartItems?: IShoppingCarItem[]) => {

    try {
      screenNotifications.fireProcessing({
        title: 'Procesando tu compra...',
        text: 'Por favor, espera mientras finalizamos tu compra.'
      });

      // Usar los elementos del carrito proporcionados o el estado global
      const itemsToProcess = cartItems || shoppingCarItems;

      const response = await shoppingCartService.buyCourses(
        itemsToProcess,
        getBillingInfo(),
        processType,
        BUY_ACTION.FOR_MYSELF,
        auth?.user?.data?.id ?? null
      );

      // Cerrar el diálogo de procesamiento
      screenNotifications.close();

      if (!response) {
        return; // El servicio ya muestra un mensaje de error
      }

      if (response.status === 'error') {
        screenNotifications.fire({
          status: 'error',
          message: response.message || 'Ha ocurrido un error al procesar tu compra.'
        });
        return;
      }

      if (response.status === 'success') {
        switch (response.processPurchase) {
          case PROCESS_PURCHASE.VERIFY_PURCHASE:
            if (response.review) {
              // Mostrar la revisión de compra
              setModalReviewData(response.review);
              setModalReviewDataState(true);
            }
            break;

          case PROCESS_PURCHASE.REDIRECT:
            if (response.redirectUrl) {
              // Limpiar el carrito antes de redirigir
              dispatch(shoppingCarChange({ action: 'remove', data: [] }));

              // Mostrar mensaje de éxito antes de redirigir
              screenNotifications.fire({
                status: 'success',
                message: 'Redirigiendo a la pasarela de pago...'
              }, {
                timer: 2000,
                showConfirmButton: false
              });

              // Redirigir después de un breve retraso
              await sleep(2000);
              window.location.href = response.redirectUrl;
            } else {
              screenNotifications.fire({
                status: 'error',
                message: 'No se ha recibido la URL de redirección para el pago.'
              });
            }
            break;

          default:
            screenNotifications.fire({
              status: 'error',
              message: 'Respuesta no reconocida del servidor.'
            });
            break;
        }
      }
    } catch (error) {
      screenNotifications.close();
      screenNotifications.fire({
        status: 'error',
        message: 'Ha ocurrido un error inesperado. Por favor, intenta nuevamente.'
      });
      console.error('Error en el proceso de compra:', error);
    }
  }

  const onCloseModalReview = (force: boolean = false, updatedCart?: IShoppingCarItem[]) => {
    setModalReviewDataState(false)
    if (force) {
      // TODO: Ver si aun salta la modal
      // purchaseProcess(force, updatedCart)
    }
  }

  const renderStepTitle = () => {
    switch (currentStep) {
      case 'cart':
        return 'Carrito de compras';
      case 'billing':
        return 'Información de facturación';
      case 'confirmation':
        return 'Confirmación de compra';
      case 'payment':
        return 'Pago';
      default:
        return '';
    }
  }

  const renderBillingForm = () => {
    return (
      <BillingForm
        onFormDataChange={handleBillingFormChange}
        totalPrice={getTotalPrice()}
        userDocNumber={''}
        initialData={billingData || undefined}
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'cart':
        return renderItems();
      case 'billing':
        return renderBillingForm();
      case 'confirmation':
        return (
          <OrderSummaryConfirmation
            items={shoppingCarItems}
            billingData={billingData as BillingFormData}
            onTermsAcceptedChange={setTermsAccepted}
            termsAccepted={termsAccepted}
            reviewData={reviewData}
            onBuyActionChange={(identifier, buyAction) => {
              // Actualizar el tipo de compra para un item específico
              const updatedItems = shoppingCarItems.map(item => {
                if (item.identifier === identifier) {
                  return { ...item, buyAction };
                }
                return item;
              });
              dispatch(shoppingCarChange({ action: 'add', data: updatedItems }));
            }}
          />
        );
      default:
        return null;
    }
  }

  // Función para verificar si algún producto es "Para mí"
  const hasItemForMyself = () => {
    return shoppingCarItems.some(item => item.buyAction === BUY_ACTION.FOR_MYSELF || item.buyAction === BUY_ACTION.FOR_ME_AND_OTHERS);
  }

  // Función para renderizar el botón adecuado según el paso y los productos
  const renderActionButton = () => {
    // Solo usar ButtonWithLogin en el último paso (confirmation) y si hay productos "Para mí"
    const needsLogin = ['billing', 'confirmation'].includes(currentStep) && hasItemForMyself();

    if (needsLogin) {
      return (
        <ButtonWithLogin
          variant='primary'
          onClick={handleMainButtonClick}
          styleButton={{width: '100%'}}
        >
          {getButtonTextByStep()}
        </ButtonWithLogin>
      );
    } else {
      return (
        <ButtonComponent
          variant='primary'
          onClick={handleMainButtonClick}
          style={{width: '100%'}}
        >
          {getButtonTextByStep()}
        </ButtonComponent>
      );
    }
  }

  const renderBoxResumen = (classname: string) => {
    let component: React.ReactNode = null
    if (shoppingCarItems.length > 0) {
      const totalPrice = getTotalPrice();
      const iva = totalPrice * 0.19;
      const totalWithTaxes = totalPrice + iva;
      component = (
        <div className='shopping_cart_screen--box-resumen'>
          <h3 className='shopping-cart-resumen-title'>Tu orden</h3>
          <hr className='separator' />
          <div className='shopping_cart_summary_container'>
            <div className='shopping_cart_summary_item'>
              <span>{getProductsCount()}</span>
              <span>{formatPrice(totalPrice)}</span>
            </div>
            <div className='shopping_cart_summary_item'>
              <span>Impuestos</span>
              <span>{formatPrice(iva)}</span>
            </div>
            <div className='shopping_cart_summary_item'>
              <span className='subtotal'>Total</span>
              <span>{formatPrice(totalWithTaxes)}</span>
            </div>
          </div>
          <div className='shopping_cart_summary_action'>
            {renderActionButton()}
          </div>
        </div>
      )
    }
    return component
  }

  return (
    <PublicAppContainer>
      <div>
        <div className="shopping_cart_screen--layout">
          {shoppingCarItems.length > 0 && (
            <StepperComponent
              steps={CART_STEPS}
              currentStep={currentStep}
              onStepChange={handleStepChange}
              className="shopping-cart-stepper"
            />
          )}
          {shoppingCarItems.length > 0 ? (
            <>
              <TextComponent type="title" as='h2' textTransform="none" className='shopping_cart_title'>
                {renderStepTitle()}
              </TextComponent>
              <div className='shopping_cart_wrapper'>
                <div className='shopping_cart_items_container'>
                  {renderStepContent()}
                  {/* {renderItems()} */}
                </div>
                <div className='shopping_cart_summary_container'>
                  {renderBoxResumen('desktop')}
                </div>
              </div>
            </>
          ) : (
            renderShoppingCartEmpty()
          )}
        </div>
      </div>


      {modalReviewDataState && (
        <ModalReviewPurchase
          open={modalReviewDataState}
          onClose={onCloseModalReview}
          data={modalReviewData}
        />
      )}
    </PublicAppContainer>
  )
}

export default ShoppingCart
